/* ============================================================
   SINGSPACE ADMIN - PAGE PARAMÈTRES (v3 Soft Professional)
   ============================================================ */

import { Bell, ChevronRight, Cloud, Lock, RefreshCw, Save, Settings, User as UserIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CloudIntegrationSection } from '../components/settings/CloudIntegrationSection';
import { NotificationsSection } from '../components/settings/NotificationsSection';
import { ProfileSection } from '../components/settings/ProfileSection';
import { SecuritySection } from '../components/settings/SecuritySection';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { SettingsTab } from '../types/settings.types';

export function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [isSaving, setIsSaving] = useState(false);

  const accountTabs: { id: SettingsTab; label: string; icon: any; desc: string }[] = [
    { id: 'profile', label: 'Mon Profil', icon: UserIcon, desc: 'Identité et avatar' },
    { id: 'security', label: 'Sécurité', icon: Lock, desc: 'Accès au compte' },
    { id: 'notifications', label: 'Notifications', icon: Bell, desc: 'Alertes système' },
  ];

  const platformTabs: { id: SettingsTab; label: string; icon: any; desc: string }[] = [
    { id: 'cloud-integrations', label: 'Intégrations Cloud', icon: Cloud, desc: 'Google Drive' },
  ];

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      toast.success('Modifications enregistrées');
    }, 800);
  };

  const renderTab = (tab: { id: SettingsTab; label: string; icon: any; desc: string }) => {
    const active = activeTab === tab.id;
    const Icon = tab.icon;
    return (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
          active ? 'bg-[#00303c] text-white' : 'bg-transparent text-slate-500 hover:bg-slate-50'
        }`}
      >
        <div
          className={`w-8 h-8 rounded flex items-center justify-center ${
            active ? 'bg-[#00758d] text-white' : 'bg-slate-100 text-slate-400'
          }`}
        >
          <Icon size={16} />
        </div>
        <div className="text-left flex-1">
          <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-slate-900'}`}>{tab.label}</p>
          <p className={`text-[11px] ${active ? 'text-white/60' : 'text-slate-500'}`}>{tab.desc}</p>
        </div>
        <ChevronRight size={14} className={active ? 'opacity-100' : 'opacity-0'} />
      </button>
    );
  };

  return (
    <div className="space-y-6 pb-12">
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#00758d] mb-1">
            <Settings size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Configuration</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Paramètres</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">
            Gérez votre profil et les intégrations de la plateforme
          </p>
        </div>

        <Button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="h-11 px-6 rounded-xl bg-[#00758d] hover:bg-[#00303c] text-white font-bold transition-colors"
        >
          {isSaving ? <RefreshCw className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {isSaving ? 'Enregistrement...' : 'Sauvegarder les changements'}
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-8">
        {/* --- SIDEBAR --- */}
        <div className="col-span-12 lg:col-span-3 space-y-6">
          <div className="space-y-2">
            <p className="px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">Compte Utilisateur</p>
            <nav className="space-y-1">{accountTabs.map(renderTab)}</nav>
          </div>

          <div className="space-y-2">
            <p className="px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">Services Cloud</p>
            <nav className="space-y-1">{platformTabs.map(renderTab)}</nav>
          </div>

          {/* User Info */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00303c] text-white flex items-center justify-center font-bold text-sm">
                {user?.name?.substring(0, 2).toUpperCase()}
              </div>
              <div className="overflow-hidden">
                <p className="font-semibold text-sm text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>

        {/* --- CONTENT AREA --- */}
        <div className="col-span-12 lg:col-span-9 bg-white border border-slate-200 rounded-2xl p-8 min-h-[500px]">
          {activeTab === 'profile' && <ProfileSection />}
          {activeTab === 'security' && <SecuritySection />}
          {activeTab === 'notifications' && <NotificationsSection />}
          {activeTab === 'cloud-integrations' && <CloudIntegrationSection />}
        </div>
      </div>
    </div>
  );
}
